import React, { useEffect, useRef } from 'react';
import { Footer as HuiFooter } from '@digitools/honest-ui';
import useTranslate from '../../hooks/useTranslate';
import { FooterText } from './FooterText';
import useTealium from '../../hooks/useTealium';
import { TEALIUM_EVENT } from '../../constants/tealiumConstants';
import useAuthentication from '../../hooks/useAuthentication';
import CookieConsent from './CookieConsent';
import useExperience from 'src/hooks/useExperience';
import { Experience } from 'src/types/Experience';
import { footerSocialLinks } from './footerBrandLinks';
import { useFooterColumns } from './footerColumns';
// @ts-ignore
import styles from './../../app/App.scss';
const COBROWSING_SCRIPT_ID = 'cobrowsing-embed-script';
const COBROWSING_IFRAME_ID = 'recursive-social-plugin';
const COBROWSING_SRC = 'https://embed.rcrsv.io/TdjblSkBgenyFlluFfcUBHyxRY87tdXtWZlfYo582d5EUqo0g9ON1U7uQnMSgaDn?ui=realtime-join-code-bottom-bar';
const useCobrowsingEmbed = (containerRef) => {
    const { trackEvent } = useTealium();
    useEffect(() => {
        const moveIframe = () => {
            const iframe = document.getElementById(COBROWSING_IFRAME_ID);
            if (iframe && containerRef.current && !containerRef.current.contains(iframe)) {
                containerRef.current.appendChild(iframe);
            }
        };
        if (!document.getElementById(COBROWSING_SCRIPT_ID)) {
            const script = document.createElement('script');
            script.id = COBROWSING_SCRIPT_ID;
            script.src = COBROWSING_SRC;
            script.async = true;
            document.body.appendChild(script);
        }
        const observer = new MutationObserver(moveIframe);
        observer.observe(document.body, { childList: true, subtree: true });
        moveIframe();
        const handleMessage = (e) => {
            if (e.origin !== 'https://widgets.rcrsv.io')
                return;
            try {
                const parsed = JSON.parse(e.data);
                if (parsed?.data?.method === 'session.start') {
                    trackEvent({
                        event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
                        event_name: 'Cobrowse Session Start',
                        event_type: TEALIUM_EVENT.EVENT_TYPE.FOOTER,
                    });
                }
            }
            catch { }
        };
        window.addEventListener('message', handleMessage);
        return () => {
            observer.disconnect();
            window.removeEventListener('message', handleMessage);
        };
    }, []);
};
const Footer = () => {
    const { t } = useTranslate();
    const { experience, language } = useExperience();
    const { isAuthenticated } = useAuthentication();
    const { trackEvent } = useTealium();
    const footerRef = useRef(null);
    // Mirrors FooterNavigation.tsx's trackFooterEvent: always logs the link's
    // English label regardless of UI language, so analytics data stays
    // consistent with what the legacy widget reported.
    // NOTE: only covers links resolved from the live nav-menu API (via
    // resolveNavItem) - the "Profile and support" column's links are built
    // directly in footerColumns.ts and aren't tracked yet. Known gap, tracked
    // separately from this migration.
    const onNavigate = (navLink) => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_name: navLink.label.english,
            event_type: TEALIUM_EVENT.EVENT_TYPE.HOME_PAGE,
            event_version: TEALIUM_EVENT.EVENT_TYPE.FOOTER,
        });
    };
    const columns = useFooterColumns(onNavigate);
    useCobrowsingEmbed(footerRef);
    const legalNavItems = [
        { label: t(FooterText.accessibility), url: '/public/accessibility' },
        { label: t(FooterText.companyOverview), url: 'https://www.lfg.com/public/aboutus/companyoverview', externalLink: true },
        { label: t(FooterText.privacy), url: 'https://www.lfg.com/privacy', externalLink: true },
        { label: t(FooterText.legal), url: 'https://www.lfg.com/legal', externalLink: true },
        { label: t(FooterText.reportFraud), url: 'https://www.lincolnfinancial.com/public/general/support/reportfraud/form', externalLink: true },
    ];
    const copyright = (<div className='copyright'>
      {`© ${new Date().getFullYear()} Lincoln National Corporation. All rights reserved`}
    </div>);
    const disclaimer = (<>
      {experience === Experience.EMPLOYEE &&
            <div className='hui-mb-md'>
          {t(FooterText.contentEE)}
        </div>}
      {experience === Experience.EMPLOYER &&
            <div className='hui-mb-md'>
          {t(FooterText.contentER)}
        </div>}
      <div>
        <CookieConsent />
      </div>
    </>);
    return (<div ref={footerRef} data-testid={'mlp-footer'} data-public={true} className={styles.footer}>
      <HuiFooter columns={columns} legalNavItems={legalNavItems} socialLinks={footerSocialLinks} copyright={copyright} disclaimer={disclaimer} backgroundColor={isAuthenticated ? 'garnet' : 'dark'} language={language}/>
    </div>);
};
export default Footer;
